import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Play, Pause, RotateCcw, Check, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import useQuranStore from '../store/quranStore';
import { quranApi } from '../lib/api';

const RECITERS = [
  { id: '1', name: 'Mishary Al Afasy' },
  { id: '2', name: 'Abu Bakr Al Shatri' },
  { id: '3', name: 'Nasser Al Qatami' },
  { id: '4', name: 'Yasser Al Dosari' },
  { id: '5', name: 'Hani Ar Rifai' },
];

export default function MemorizationMode({
  isOpen,
  onClose,
  surahNo,
  ayahNo,
  surahName,
  verse,
}) {
  const audioRef = useRef(null);
  const { 
    reciterId, 
    setReciterId,
    showDiacritics,
    updateMemorizationProgress,
    getMemorizationStatus,
  } = useQuranStore();

  const [audioData, setAudioData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentRep, setCurrentRep] = useState(0);
  const [targetReps, setTargetReps] = useState(5);
  const [showArabic, setShowArabic] = useState(true);
  const [showTranslation, setShowTranslation] = useState(true);
  const [autoRepeat, setAutoRepeat] = useState(true);
  const [loading, setLoading] = useState(false);

  // Get current memorization status
  const memStatus = getMemorizationStatus(surahNo, ayahNo);
  const totalReps = memStatus.repetitions + currentRep;

  // Load audio data
  useEffect(() => {
    const loadAudio = async () => {
      setLoading(true);
      try {
        const data = await quranApi.getVerseAudio(surahNo, ayahNo);
        setAudioData(data);
      } catch (err) {
        console.error('Error loading audio:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen && surahNo && ayahNo) {
      loadAudio();
    }
  }, [isOpen, surahNo, ayahNo]);

  // Get audio URL
  const getAudioUrl = useCallback(() => {
    if (!audioData || !audioData[reciterId]) return null;
    return audioData[reciterId].originalUrl || audioData[reciterId].url;
  }, [audioData, reciterId]);

  // Update audio source when reciter changes
  useEffect(() => {
    const audio = audioRef.current;
    const url = getAudioUrl();
    if (audio && url) {
      audio.src = url;
      audio.load();
    }
  }, [audioData, reciterId, getAudioUrl]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      const newRep = currentRep + 1;
      setCurrentRep(newRep);
      
      if (autoRepeat && newRep < targetReps) {
        // Play again after a short delay
        setTimeout(() => {
          audio.currentTime = 0;
          audio.play().catch(() => {});
          setIsPlaying(true);
        }, 1000);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentRep, targetReps, autoRepeat]);

  // Play/Pause toggle
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
  };

  // Reset current session
  const resetSession = () => {
    setCurrentRep(0);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
  };

  // Save progress and close
  const saveAndClose = () => {
    if (currentRep > 0) {
      updateMemorizationProgress(surahNo, ayahNo, currentRep);
    }
    onClose();
  };

  // Get status color and label
  const getStatusInfo = () => {
    if (totalReps >= 10) return { color: 'text-green-500', bg: 'bg-green-500', label: 'Memorized' };
    if (totalReps >= 5) return { color: 'text-amber-500', bg: 'bg-amber-500', label: 'Reviewing' };
    return { color: 'text-blue-500', bg: 'bg-blue-500', label: 'Learning' };
  };

  const statusInfo = getStatusInfo();
  const progressPercent = Math.min((currentRep / targetReps) * 100, 100);

  // Arabic text
  const arabicText = showDiacritics ? verse?.arabic1 : verse?.arabic2;

  return (
    <Dialog open={isOpen} onOpenChange={saveAndClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="memorization-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Memorization Mode</span>
            <span className={`text-sm font-normal px-2 py-1 rounded-full ${statusInfo.bg}/10 ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </DialogTitle>
          <DialogDescription>
            {surahName} - Ayah {ayahNo} · Total repetitions: {totalReps}
          </DialogDescription>
        </DialogHeader>

        <audio ref={audioRef} preload="metadata" />

        <div className="space-y-6 py-4">
          {/* Arabic Text */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-0 right-0 text-xs"
              onClick={() => setShowArabic(!showArabic)}
            >
              {showArabic ? 'Hide' : 'Show'} Arabic
            </Button>
            {showArabic && (
              <div className="font-arabic text-3xl sm:text-4xl leading-[2.5] text-right pt-8" dir="rtl">
                {arabicText}
              </div>
            )}
            {!showArabic && (
              <div className="h-24 flex items-center justify-center text-muted-foreground border border-dashed rounded-lg">
                Arabic text hidden for memorization practice
              </div>
            )}
          </div>

          {/* Translation */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-0 right-0 text-xs"
              onClick={() => setShowTranslation(!showTranslation)}
            >
              {showTranslation ? 'Hide' : 'Show'} Translation
            </Button>
            {showTranslation && (
              <p className="text-muted-foreground pt-8">
                {verse?.english}
              </p>
            )}
            {!showTranslation && (
              <div className="h-16 flex items-center justify-center text-muted-foreground border border-dashed rounded-lg mt-8">
                Translation hidden
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Session Progress</span>
              <span className="font-medium">{currentRep} / {targetReps} repetitions</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Reciter selector */}
            <Select value={reciterId} onValueChange={setReciterId}>
              <SelectTrigger className="w-full sm:w-48" data-testid="memorization-reciter-select">
                <SelectValue placeholder="Select reciter" />
              </SelectTrigger>
              <SelectContent>
                {RECITERS.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Target reps */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Target:</span>
              <div className="flex items-center border rounded-md">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setTargetReps(Math.max(1, targetReps - 1))}
                  disabled={targetReps <= 1}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center font-medium">{targetReps}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setTargetReps(targetReps + 1)}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Auto repeat toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={autoRepeat}
                onChange={(e) => setAutoRepeat(e.target.checked)}
                className="rounded border-border"
              />
              Auto-repeat
            </label>
          </div>

          {/* Playback controls */}
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={resetSession}
              data-testid="memorization-reset-btn"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>

            <Button
              size="lg"
              className="w-16 h-16 rounded-full"
              onClick={togglePlay}
              disabled={loading || !audioData}
              data-testid="memorization-play-btn"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6 ml-1" />
              )}
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={saveAndClose}
              className="text-green-600 border-green-600 hover:bg-green-50"
              data-testid="memorization-done-btn"
            >
              <Check className="w-5 h-5" />
            </Button>
          </div>

          {/* Tips */}
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">Memorization Tips:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Listen to the verse multiple times to familiarize with the pronunciation</li>
              <li>Hide the Arabic text and try to recite along with the audio</li>
              <li>Aim for at least 10 repetitions for strong memorization</li>
              <li>Practice regularly - consistency is key!</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
